/**
 * Per-tile "starter template subtitle" dismissal (S21). Backed by the
 * `DashboardUiState` sidecar's `dismissedSubtitles` field — unlike
 * `paramValues`/`alertState` this is a plain array, FULL-REPLACE on write
 * (see `dashboardRepository.ts` `setUiState`: only the record fields go
 * through the per-key `mergeRecords` deep-merge), so every write here sends
 * the complete current set. Loaded lazily per dashboard, mirroring
 * `dashboardAlertStore.ts`'s `ensureLoaded` shape.
 */
import { create } from 'zustand';

type DashboardStarterStoreState = {
  byDashboard: Record<string, Set<string>>;
  loaded: Set<string>;
  loading: Map<string, Promise<void>>;
  ensureLoaded: (dashboardId: string) => Promise<void>;
  dismiss: (dashboardId: string, tileId: string) => Promise<void>;
};

const api = () => window.electronAPI?.dashboards;

export const useDashboardStarterStore = create<DashboardStarterStoreState>((set, get) => ({
  byDashboard: {},
  loaded: new Set(),
  loading: new Map(),

  ensureLoaded: async (dashboardId) => {
    if (get().loaded.has(dashboardId)) return;
    const inFlight = get().loading.get(dashboardId);
    if (inFlight) return inFlight;

    const promise = (async () => {
      const bridge = api();
      const uiState = bridge ? await bridge.getUiState(dashboardId) : { version: 1 as const };
      set((s) => ({
        byDashboard: {
          ...s.byDashboard,
          [dashboardId]: new Set(uiState.dismissedSubtitles ?? []),
        },
        loaded: new Set(s.loaded).add(dashboardId),
      }));
    })();

    set((s) => ({ loading: new Map(s.loading).set(dashboardId, promise) }));
    await promise;
  },

  dismiss: async (dashboardId, tileId) => {
    const current = get().byDashboard[dashboardId] ?? new Set<string>();
    if (current.has(tileId)) return;
    const next = new Set(current).add(tileId);
    set((s) => ({ byDashboard: { ...s.byDashboard, [dashboardId]: next } }));
    const bridge = api();
    if (!bridge) return;
    await bridge.setUiState(dashboardId, { dismissedSubtitles: [...next] });
  },
}));
